import { gql } from "../../../../__generated__";
import { useMutation } from "@apollo/client";
import { useState } from "react";
import { notify, notifyError } from "../../../../utils/toast";
import { useNavigate } from "react-router-dom";
import BackArrow from "../../../../components/common/back-arrow";
import FadeInWrapper from "../../../../components/common/fade-in-wrapper";
import LoadingSpinnerInline from "../../../../components/common/loading-spinner/loading-spinner-inline";

const SEND_SELLER_AUTH_NUMBER = gql(`
  mutation sendSellerAuthNumber($phoneNumber: String!) {
    sendSellerAuthNumber(phoneNumber: $phoneNumber) {
      ok
      error
    }
  }
`);

const VERIFY_SELLER_AUTH_NUMBER = gql(`
  mutation verifySellerAuthNumber($phoneNumber: String!, $authNumber: String!) {
    verifySellerAuthNumber(phoneNumber: $phoneNumber, authNumber: $authNumber) {
      ok
      error
    }
  }
`);

export default function PasswordReset() {
  const [sendSellerAuthNumber, { loading: isSendLoading }] = useMutation(
    SEND_SELLER_AUTH_NUMBER
  );
  const [verifySellerAuthNumber, { loading: isVerifyLoading }] = useMutation(
    VERIFY_SELLER_AUTH_NUMBER
  );
  const navigate = useNavigate();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [authNumber, setAuthNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const handleSendAuthNumber = (e: React.FormEvent) => {
    e.preventDefault();

    if (phoneNumber.length !== 11) {
      setPhoneError("연락처는 11자리여야 합니다.");
      return;
    }
    setPhoneError("");

    sendSellerAuthNumber({ variables: { phoneNumber } }).then((res) => {
      if (res.data?.sendSellerAuthNumber.ok) {
        notify("인증번호가 발송되었습니다.");
      } else {
        notifyError(
          res.data?.sendSellerAuthNumber.error ||
            "인증번호 발송에 실패했습니다."
        );
      }
    });
  };

  const handleVerifyAuthNumber = (e: React.FormEvent) => {
    e.preventDefault();

    verifySellerAuthNumber({
      variables: { phoneNumber, authNumber },
    }).then((res) => {
      if (res.data?.verifySellerAuthNumber.ok) {
        notify(
          "비밀번호가 연락처와 동일하게 변경되었습니다.\n로그인 후 비밀번호를 변경해주세요!"
        );
        setTimeout(() => {
          navigate("/boss/login");
        }, 1000);
      } else {
        notifyError(
          res.data?.verifySellerAuthNumber.error || "인증에 실패했습니다."
        );
      }
    });
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <FadeInWrapper className="w-full max-w-md bg-white p-6 pb-10">
        <div className="mb-4" onClick={() => navigate(-1)}>
          <BackArrow />
        </div>

        <div className="mb-6 text-center">
          <h2 className="text-2xl font-semibold text-black mb-2">
            비밀번호 재설정
          </h2>
          <p className="text-sm text-[#757575]">
            가입된 연락처로 인증번호를 발송해드릴게요.
            <br />
            발송된 인증번호를 입력해주세요.
          </p>
        </div>

        <div className="space-y-4">
          <form onSubmit={handleSendAuthNumber}>
            <label
              htmlFor="phoneNumber"
              className="block text-sm font-medium text-black mb-2 pl-2"
            >
              연락처
            </label>
            <div className="relative">
              <input
                type="tel"
                id="phoneNumber"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={11}
                value={phoneNumber}
                onChange={(e) => {
                  const onlyNums = e.target.value.replace(/[^0-9]/g, "");
                  if (onlyNums.length <= 11) {
                    setPhoneNumber(onlyNums);
                    if (phoneError) setPhoneError("");
                  }
                }}
                className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:border-primary text-sm text-gray-700"
                placeholder="숫자만 입력해주세요"
                required
              />
              <button
                type="submit"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-10 w-14 flex items-center justify-center text-white bg-primary rounded-lg active:brightness-90 focus:outline-none transition-colors disabled:opacity-50"
                disabled={isSendLoading}
              >
                {isSendLoading ? <LoadingSpinnerInline /> : <span>발송</span>}
              </button>
            </div>
            {phoneError && (
              <p className="text-red-500 text-sm mt-1 pl-2">{phoneError}</p>
            )}
          </form>

          <form onSubmit={handleVerifyAuthNumber} className="space-y-4">
            <div>
              <label
                htmlFor="authNumber"
                className="block text-sm font-medium text-black mb-2 pl-2"
              >
                인증번호
              </label>
              <input
                type="text"
                id="authNumber"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={authNumber}
                onChange={(e) => {
                  const onlyNums = e.target.value.replace(/[^0-9]/g, "");
                  if (onlyNums.length <= 6) {
                    setAuthNumber(onlyNums);
                  }
                }}
                className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:border-primary text-sm text-gray-700"
                placeholder="인증번호 6자리를 입력해주세요"
                required
              />
            </div>
            <button
              type="submit"
              className={`w-full py-3 rounded-xl text-white font-bold ${
                isVerifyLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-primary hover:brightness-90 transition duration-150"
              }`}
              disabled={isVerifyLoading}
            >
              {isVerifyLoading ? <LoadingSpinnerInline /> : "인증"}
            </button>
          </form>
        </div>
      </FadeInWrapper>
    </div>
  );
}
